import type { GeocodingResult } from '@iheartjane/geolocation';
import {
  geocode,
  getAddresses,
  googleMapsLoader,
  reverseGeocode as googleReverseGeocode,
} from '@iheartjane/geolocation';
import type { Nullable, ShopperReservationMode } from '@iheartjane/types';
import { useCallback } from 'react';

import type { AutocompleteAddress } from '@jane/bloom/types/schemas/bloom/storeSwitcher';
import { logError } from '@jane/bloom/utils/logError';

import { useRootLoaderData } from './useRootLoaderData';

export const useGooglePlaces = () => {
  const {
    appConfig: { googlePlacesApiKey },
  } = useRootLoaderData();

  const googleLoader = useCallback(() => {
    return googleMapsLoader(googlePlacesApiKey);
  }, [googlePlacesApiKey]);

  const getGoogleAddresses = async (
    input: string
  ): Promise<AutocompleteAddress[]> => {
    try {
      return getAddresses({ input, loader: googleLoader });
    } catch (e) {
      logError(e);
      return [];
    }
  };

  const getAddressDetails = async (
    address: string
  ): Promise<GeocodingResult> => {
    return await geocode({
      loader: googleLoader,
      searchCriteria: address,
    });
  };

  const validateLocation = async (
    address: string,
    reservationMode: ShopperReservationMode
  ): Promise<{
    error: string | null;
  }> => {
    const location = await geocode({
      loader: googleLoader,
      searchCriteria: address,
    });

    const delivery = reservationMode === 'delivery';
    const { city, cityState, street } = location;

    if (!city || cityState === 'Unknown') {
      return { error: 'noCity' };
    }
    if (delivery && !street) {
      return { error: 'deliveryAddress' };
    }

    return { error: null };
  };

  const reverseGeocode = async (
    latitude: number,
    longitude: number
  ): Promise<Nullable<GeocodingResult>> => {
    return await googleReverseGeocode({
      loader: googleLoader,
      searchCriteria: {
        lat: latitude,
        long: longitude,
      },
    });
  };

  return {
    getAddressDetails,
    getGoogleAddresses,
    reverseGeocode,
    validateLocation,
  };
};
